'use client';

import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { initializeAppInsights } from '../lib/insights/appInsights';
import { initializeMinimalAppInsights } from '../lib/insights/appInsightsMinimal';

interface AppInsightsProviderProps {
  children: ReactNode;
}

const InsightsContext = createContext<{
  initialized: boolean;
}>({
  initialized: false
});

export const AppInsightsProvider: React.FC<AppInsightsProviderProps> = ({ children }) => {
  const [initialized, setInitialized] = React.useState(false);

  useEffect(() => {
    try {
      const { appInsights, reactPlugin } = initializeAppInsights();
      
      if (appInsights && reactPlugin) {
        setInitialized(true);
        if (process.env.NODE_ENV === 'development') {
          console.log('Application Insights initialized successfully');
        }
      } else {
        // Fallback to minimal Application Insights
        const minimalInsights = initializeMinimalAppInsights();
        if (minimalInsights) {
          setInitialized(true);
          if (process.env.NODE_ENV === 'development') {
            console.log('Minimal Application Insights initialized as fallback');
          }
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Both full and minimal Application Insights failed to initialize');
          }
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Application Insights initialization error:', error);
      }
      // Try minimal version as last resort
      try {
        const minimalInsights = initializeMinimalAppInsights();
        if (minimalInsights) {
          setInitialized(true);
          if (process.env.NODE_ENV === 'development') {
            console.log('Minimal Application Insights initialized after full version failed');
          }
        }
      } catch (minimalError) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Minimal Application Insights also failed:', minimalError);
        }
      }
    }
  }, []);

  const { appInsights, reactPlugin } = initializeAppInsights();

  if (!appInsights || !reactPlugin) {
    // Fallback when Application Insights is not available
    return (
      <InsightsContext.Provider value={{ initialized: false }}>
        {children}
      </InsightsContext.Provider>
    );
  }

  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <InsightsContext.Provider value={{ initialized }}>
        {children}
      </InsightsContext.Provider>
    </AppInsightsContext.Provider>
  );
};

export const useInsightsStatus = () => {
  const context = useContext(InsightsContext);
  if (!context) {
    throw new Error('useInsightsStatus must be used within an AppInsightsProvider');
  }
  return context;
};